import { useState, useCallback } from 'react';
import {
  RawGaushala,
  NormalizedGaushala,
  GeocodingProgress,
  GeocodingOptions,
} from '../types/gaushalaMap.types';
import {
  separateGaushalas,
  normalizeGaushalaWithDbCoords,
  normalizeGaushalaWithGeocoding,
  normalizeGaushalaWithFallback,
  buildGeocodingQueue,
  mergeWithGeocodingResults,
  spreadOverlappingMarkers,
} from '../services/dataTransformService';
import {
  batchGeocode,
  geocodeWithFallback,
} from '../services/geocodingService';

/**
 * Custom Hook: useGeocoding
 *
 * Handles the conversion of raw gaushalas to normalized gaushalas
 * with guaranteed latitude and longitude coordinates
 *
 * Process:
 * 1. Separate gaushalas into those with and without coordinates
 * 2. Normalize those with coordinates immediately
 * 3. Geocode those without coordinates (with progress tracking)
 * 4. Merge results and return normalized array
 */

export const useGeocoding = (options: GeocodingOptions = {}) => {
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [progress, setProgress] = useState<GeocodingProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Normalize array of gaushalas, geocoding those that need it
   */
  const normalizeGaushalas = useCallback(
    async (rawGaushalas: RawGaushala[]): Promise<NormalizedGaushala[]> => {
      try {
        setIsGeocoding(true);
        setError(null);
        setProgress(null);

        if (!rawGaushalas || rawGaushalas.length === 0) {
          return [];
        }

        const normalized: NormalizedGaushala[] = [];

        // PHASE 1: Separate gaushalas into two groups
        const { withCoordinates, needsGeocoding } =
          separateGaushalas(rawGaushalas);

        // PHASE 2: Normalize those with coordinates (no geocoding needed)
        console.log(
          `[Geocoding] Found ${withCoordinates.length} gaushalas with coordinates`
        );
        const normalizedWithCoords = withCoordinates.map((g) =>
          normalizeGaushalaWithDbCoords(g)
        );
        normalized.push(...normalizedWithCoords);

        // PHASE 3: Geocode those without coordinates
        if (needsGeocoding.length > 0) {
          console.log(
            `[Geocoding] Need to geocode ${needsGeocoding.length} gaushalas`
          );

          try {
            // Batch geocode with progress tracking
            const geocodingQueue = buildGeocodingQueue(needsGeocoding);

            const geoResults = await batchGeocode(geocodingQueue, (current, total) => {
              const newProgress: GeocodingProgress = {
                current,
                total,
                percentage: Math.round((current / total) * 100),
              };
              setProgress(newProgress);

              if (options?.onProgress) {
                options.onProgress(newProgress);
              }
            });

            // Merge geocoding results with gaushalas
            const normalizedGeocoded = mergeWithGeocodingResults(
              needsGeocoding,
              geoResults
            );
            normalized.push(...normalizedGeocoded);

            console.log(
              `[Geocoding] Successfully geocoded ${geoResults.length} gaushalas`
            );
          } catch (geocodingError) {
            console.error('[Geocoding] Batch geocoding error:', geocodingError);

            // Fallback: Add all with fallback coordinates
            const fallbackNormalized = needsGeocoding.map((g) =>
              normalizeGaushalaWithFallback(g)
            );
            normalized.push(...fallbackNormalized);

            setError(
              'Some gaushalas could not be geocoded and are shown at approximate location'
            );
          }
        }

        setProgress(null);

        // STEP 1: Filter out test/fake addresses
        // NOTE: Building numbers like "1-8-448" or "123, Main Street" are VALID
        // Only filter addresses with clear test/fake indicators
        const TEST_ADDRESS_KEYWORDS = [
          'test',
          'proxy',
          'sample',
          'demo',
          'example',
          'fake',
          'dummy',
          'lorem',
          'ipsum',
          'xxx',
          'abc street',
          'xyz road',
        ];

        const validGaushalas = normalized.filter((g) => {
          const addressLower = g.address.toLowerCase();
          const nameLower = g.name.toLowerCase();

          // Check if address or name contains test keywords
          const isTestAddress = TEST_ADDRESS_KEYWORDS.some(
            (pattern) =>
              addressLower.includes(pattern) || nameLower.includes(pattern)
          );

          if (isTestAddress) {
            console.log(`[Geocoding] Filtered out test gaushala: "${g.name}" at "${g.address}"`);
            return false;
          }

          return true;
        });

        console.log(`[Geocoding] Filtered: ${normalized.length} → ${validGaushalas.length} (removed ${normalized.length - validGaushalas.length} test entries)`);

        // STEP 2: Filter out gaushalas at India center (unknown locations)
        const INDIA_CENTER_LAT = 20.5937;
        const INDIA_CENTER_LNG = 78.9629;

        const knownLocationGaushalas = validGaushalas.filter((g) => {
          const isIndiaCenter =
            Math.abs(g.latitude - INDIA_CENTER_LAT) < 0.01 &&
            Math.abs(g.longitude - INDIA_CENTER_LNG) < 0.01;

          if (isIndiaCenter) {
            console.log(`[Geocoding] Filtered out unknown location: "${g.name}" at "${g.address}"`);
            return false;
          }

          return true;
        });

        console.log(`[Geocoding] Known locations: ${knownLocationGaushalas.length}`);

        // STEP 3: Remove duplicates (same name + same location within 1km)
        const uniqueGaushalas: typeof knownLocationGaushalas = [];
        const seenLocations = new Map<string, boolean>();

        knownLocationGaushalas.forEach((g) => {
          // Create key from name + rounded coordinates (within ~1km)
          const locationKey = `${g.name.toLowerCase().trim()}_${g.latitude.toFixed(2)}_${g.longitude.toFixed(2)}`;

          if (!seenLocations.has(locationKey)) {
            seenLocations.set(locationKey, true);
            uniqueGaushalas.push(g);
          } else {
            console.log(`[Geocoding] Filtered out duplicate: "${g.name}" at "${g.address}"`);
          }
        });

        console.log(`[Geocoding] Unique gaushalas: ${uniqueGaushalas.length}`);

        // STEP 4: Spread overlapping markers so all are visible
        const spreadMarkers = spreadOverlappingMarkers(uniqueGaushalas);
        console.log(`[Geocoding] Final markers after spread: ${spreadMarkers.length}`);

        return spreadMarkers;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error during geocoding';
        console.error('[Geocoding] Error:', errorMessage);
        setError(errorMessage);
        throw err;
      } finally {
        setIsGeocoding(false);
      }
    },
    []
  );

  /**
   * Geocode a single gaushala
   * Useful for individual geocoding requests
   */
  const geocodeSingle = useCallback(
    async (
      pincode: string | null,
      address: string | null,
      name: string | null
    ) => {
      try {
        setIsGeocoding(true);
        setError(null);

        const result = await geocodeWithFallback(pincode, address, name);
        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Geocoding failed';
        setError(errorMessage);
        throw err;
      } finally {
        setIsGeocoding(false);
      }
    },
    []
  );

  /**
   * Reset all states
   */
  const reset = useCallback(() => {
    setIsGeocoding(false);
    setProgress(null);
    setError(null);
  }, []);

  return {
    normalizeGaushalas,
    geocodeSingle,
    isGeocoding,
    progress,
    error,
    reset,
  };
};

export default useGeocoding;
